// ---- her field ----
// The engine owns who is standing where and the collisions (core/field.ts).
// This says how big her field is, what shape she is in it, and the things
// that are hers: how a dish arrives — which is a flight onto the table, not a
// placement — how many the table holds and what happens to the oldest when it
// is full, and what the field does each frame.
//
// Note there is no water: setPond is never called, so every check in
// core/water.ts answers "nowhere is wet", which is what a field of dry grass is.
import * as THREE from 'three';
import {admit,bodies,elder,elderBody,fieldSpot,inFrame,separate,setField} from '../../core/field';
import {clamp,rnd} from '../../core/util';
import {scene} from '../../core/scene';
import {SOFRA,ring} from './set';
import {makeDish,throwDish,updateFlight,type Dish} from './dishes';
import type {FoodSpec} from './pack';

export const dishes=bodies as Dish[];
export {elder as tata, elderBody as tataBody, fieldSpot, inFrame};

// How many the table holds. The food STAYS once he has eaten it — a sofra is
// what has been served — so the table fills, and past eight the oldest leaves
// to make room for the next one as she makes it (serve, below). Eight is what
// fits on a 3.2 by 1.9 top with the dishes spaced by their width. It was ten on
// the round table this one replaced, and ten on this one ran plates a quarter
// of a unit into each other whenever the top was full (tata/README.md).
export const TABLE_MAX=8;

// Off the table's far edge, not at it, and off to one side of it: one of the
// spots her own orbit would pick (orbitSpot, tata.ts — 2.5 units out, 55° off
// the camera's line). Her orbit keeps her there afterwards. The table is where
// the food goes, and a grandmother standing over the middle of her own sofra
// is a grandmother the plates have to be thrown around — while a grandmother
// on the camera's centre line starts the game with a letter chip over her face.
// So: 0.95 off the table's edge, in the middle of her orbit, 55° off the
// camera's line.
const START=ring(-2.18,0.95);
elder.position.set(START.x,0,START.z);
setField({
  bounds:{x:8.0,zFar:-8.5,zNear:3.0},
  capsule:[[0.5,0.42],[1.35,0.42],[1.95,0.34]],   // up her body; she is slighter than Jiddo
  spacing:1.1,                                    // dishes sit closer together than animals stand
  // A backstop only. The engine's cap pops the oldest body out of the scene the
  // frame the one past it lands; the table is cleared in serve() instead, as
  // she makes the next one, with a shrink a child can watch — so this is never
  // reached, and if it ever were the table would still not overflow.
  cap:TABLE_MAX,
  // A dish that is bumped does nothing at all: it is a plate. This is the seam
  // that made a shared field worth having.
});

// Where it goes: **on the table**. That is what a sofra is for: she serves
// there, he comes there to eat, and between rounds it is the thing they are
// both about.
//
// Where on it: room from whatever is already down — the food stays, so by the
// tenth round it is a spread — and a real throw from where she stands rather
// than a plate set down at her own elbow.
// `half` is the dish's own half-width — spec.across/2, the same number its
// collider gets. NOT its pad: pad is footprint(), the reach of what touches the
// FLOOR, and for a plate that is a fraction of the plate (0.10 for the banana
// against a 0.62 width). Spacing by pad let two dishes sit half on top of each
// other and look like one.
const MIN_THROW=1.5;                             // shorter than this and she is putting it down, not serving it
const BOY_CLEAR=1.4;                             // and no nearer him than this, if the table leaves the room
// how far either way from the middle a dish's middle may be, across and along,
// to sit fully on the top
export const onTop=(half:number)=>({x:Math.max(0.15,SOFRA.hx-half-0.08),z:Math.max(0.15,SOFRA.hz-half-0.08)});
// A draw, and the roomiest one of its kind so far. The tiers are the order a
// spot is wanted in: on the table and in the frame, a real throw from where
// she stands, and clear of the boy.
//
// The first draw that has room still wins outright — that is what spreads the
// food over the whole top rather than heaping it wherever the arithmetic
// points. What changed is what happens when NO draw has room, which used to be
// rare and now is the common case: the food stays, so by about the seventh
// dish no draw in forty-eight has a clear gap. Falling back to the roomiest
// draw of ALL of them ignored the frame and the throw; falling back to the
// roomiest of the GOOD ones puts the plate in the largest real gap on the
// table, which is what a crowded sofra is.
type Spot={x:number;z:number;gap:number}|null;
const better=(a:Spot,b:Spot)=>!a||(!!b&&b.gap>a.gap)?b:a;
function serveSpot(half:number,away:{x:number;z:number}){
  const R=onTop(half), need=half+0.12;
  let good:Spot=null, near:Spot=null, seen:Spot=null, any:Spot=null;
  for(let i=0;i<48;i++){
    const x=SOFRA.x+rnd(-R.x,R.x), z=SOFRA.z+rnd(-R.z,R.z);   // evenly over the top
    let gap=9;
    // A dish in his hands is measured where it is coming BACK to, not where it
    // is: for the three and a half seconds of a mouthful its own spot on the
    // top looks empty, and a plate thrown into it lands under the one he is
    // about to set down. The shuffle would push the two apart afterwards, but
    // "afterwards" is a child watching one plate shove another.
    for(const e of dishes){const q=e.held?e.home:e.position;
      gap=Math.min(gap,Math.hypot(x-q.x,z-q.z)-e.spec.across*0.5);}
    const d={x,z,gap};
    any=better(any,d);
    // Where a child can see it land. Scoring "furthest from her" read well and
    // put every single throw on the rim of the table furthest from where she
    // stands — which is the near rim, since her orbit keeps her on the far
    // side, and on a phone held sideways the near rim is behind the tray.
    if(!inFrame(x,z))continue;
    seen=better(seen,d);
    if(Math.hypot(x-elder.position.x,z-elder.position.z)<MIN_THROW)continue;   // she is putting it down, not serving it
    near=better(near,d);
    // Not into his hands. He is meant to be out on his ring by now and usually
    // is, but a child who spells fast can finish a word while he is still
    // stood over the last one, and a dish that lands beside him is eaten where
    // he stands: no run, no arrival, no beat. `near` is what is left when the
    // table is small enough that every draw is near him.
    if(Math.hypot(x-away.x,z-away.z)<BOY_CLEAR)continue;
    if(d.gap>=need)return d;                  // room of its own: taken as drawn, which is what spreads them
    good=better(good,d);
  }
  return good||near||seen||any||{x:SOFRA.x,z:SOFRA.z,gap:0};   // a full table still takes one more; separate() shuffles them
}
// A dish in the air is not in the field: no collisions, and nobody walks to it
// until it has come down. That is what keeps the boy from setting off after
// something still arcing over his head.
const flying:Dish[]=[];
// ...and one on its way out is not in it either: nothing lands on the spot it
// is still shrinking from, because the spot is already free.
const leaving:Dish[]=[];
const LEAVE=520;                                 // how long the oldest takes to go
// The table is full: the oldest goes. The oldest EATEN one — he has had it, and
// it is the one nobody will miss — and only when nothing on the table has been
// eaten, the oldest of all, which he may have been running for: nearest()
// (boy.ts) drops a dish that is no longer in the field, and he turns to the
// next. What is still in the air is counted too — not that play can put two
// there (a round is at least 2.4s and a flight is 1.46s), but the ?dbg handle
// can, and counting it costs nothing.
function makeRoom(t:number){
  while(dishes.length+flying.length>=TABLE_MAX&&dishes.length){
    // ...and never the one in his hands, while there is any other to take:
    // shrinking a plate away mid-mouthful is the one way this reads as a bug.
    const d=dishes.find(e=>e.eaten&&!e.held)||dishes.find(e=>!e.held)||dishes[0];
    dishes.splice(dishes.indexOf(d),1); d.gone=t; leaving.push(d);
  }
}
// She makes it: it appears in her hands and, once she has turned to face the
// table, leaves them for it. `hands` is hers to answer every frame — she is
// still turning while she holds it.
export function serve(spec:FoodSpec,hands:()=>THREE.Vector3,away:{x:number;z:number},t:number){
  // The dish FIRST, and only then the room for it. makeDish clones the word's
  // model, which throws for a word whose .glb has not arrived — impossible
  // through play, where only a ready() word is ever dealt, and one keystroke
  // away through the ?dbg handle. Clearing the table first meant that throw
  // cost a plate and served nothing.
  const d=makeDish(spec);
  makeRoom(t);
  const s=serveSpot(spec.across*0.5,away);
  throwDish(d,hands,{x:s.x,y:SOFRA.h,z:s.z},t);
  return {dish:d,to:s};
}
export function launchDish(d:Dish){flying.push(d);}
// The newest dish nobody has eaten, or null when everything on the table has
// been. What she looks at between strolls (tata.ts) — and, with the one dish a
// round leaves, what he is coming for; he goes to whichever is nearest HIM
// (nearest, boy.ts), so with two out they can differ.
export function waiting():Dish|null{
  for(let i=dishes.length-1;i>=0;i--)if(!dishes[i].eaten)return dishes[i];
  return null;
}
export function updateField(t:number){
  for(let i=flying.length-1;i>=0;i--){
    if(updateFlight(flying[i],t)){admit(flying[i],t);flying.splice(i,1);}
  }
  // The oldest, on its way out: it shrinks to nothing where it stands and is
  // gone. Out of bodies already, so nothing collides with it or comes for it.
  for(let i=leaving.length-1;i>=0;i--){
    const d=leaving[i], k=Math.min(1,(t-d.gone)/LEAVE);
    d.scale.setScalar(d.full*Math.max(0.01,(1-k)*(1-k)));
    if(k>=1){scene.remove(d);leaving.splice(i,1);}
  }
  // No grow() here on purpose. The engine's pop-in is for something that
  // appears from nowhere, and it starts from a scale of nothing the moment a
  // body is admitted — which for a dish is the moment it LANDS, so it would be
  // full size all the way through the air and then snap to a speck and swell
  // back. The arc is the arrival; nothing else is needed.
  separate(t);
  // The engine's shuffle pushes overlapping bodies apart along the ground and
  // knows nothing about a table: a dish shoved off the edge would hang in the
  // air past its edge. So whatever it did, every dish is back on the top — and
  // at its height, which the shuffle never touches but the clamp makes certain.
  for(const d of dishes){
    if(d.held)continue;                          // up at his mouth: where it is is his to say, and he puts it back
    const R=onTop(d.spec.across*0.5);
    d.position.x=clamp(d.position.x,SOFRA.x-R.x,SOFRA.x+R.x);
    d.position.z=clamp(d.position.z,SOFRA.z-R.z,SOFRA.z+R.z);
    d.position.y=SOFRA.h;
  }
}
